// Merchant Employee Permissions — single source of truth (frontend)
// ⚠️ ต้อง sync กับ backend: go2pay_backend/config/merchantPermissions.js
import {
  faEdit, faEye, faClipboardList, faMoneyBill, faChartLine, faTicket, faUsers, faGear
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/free-solid-svg-icons';

export type PermissionKey =
  | 'can_view_dashboard'
  | 'can_create_order'
  | 'can_view_orders'
  | 'can_view_transactions'
  | 'can_manage_settlement'
  | 'can_view_settlement'
  | 'can_create_settlement'
  | 'can_view_payouts'
  | 'can_create_payout'
  | 'can_send_callback'
  | 'can_view_analytics'
  | 'can_view_tickets'
  | 'can_manage_employees'
  | 'can_manage_settings';

export type Permissions = Partial<Record<PermissionKey, boolean>>;

/**
 * ค่า fallback เมื่อ key ไม่มีใน permissions ของพนักงาน (ข้อมูลเก่า)
 * - can_view_dashboard / can_send_callback / can_view_analytics / can_view_tickets = true
 *   เพราะพนักงานเดิมใช้งานส่วนนี้ได้อยู่แล้ว ต้องไม่ตัดสิทธิ์ย้อนหลัง
 */
export const PERMISSION_FALLBACKS: Record<PermissionKey, boolean> = {
  can_view_dashboard: true,
  can_create_order: false,
  can_view_orders: false,
  can_view_transactions: false,
  can_manage_settlement: false,
  can_view_settlement: false,
  can_create_settlement: false,
  can_view_payouts: false,
  can_create_payout: false,
  can_send_callback: true,
  can_view_analytics: true,
  can_view_tickets: true,
  can_manage_employees: false,
  can_manage_settings: false
};

/** ค่าเริ่มต้นตอนสร้างพนักงานใหม่ (checkbox ใน modal) */
export const NEW_EMPLOYEE_DEFAULTS: Record<PermissionKey, boolean> = {
  ...PERMISSION_FALLBACKS,
  can_create_order: true,
  can_view_orders: true
};

/** รายการ permission ทั้งหมด — ใช้ render checkbox ใน form และ badge ใน EmployeesPage */
export const PERMISSION_META: {
  key: PermissionKey;
  labelKey: string;
  icon: IconDefinition;
  badgeClass: string;
}[] = [
  { key: 'can_view_dashboard', labelKey: 'employee.permViewDashboard', icon: faChartLine, badgeClass: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' },
  { key: 'can_create_order', labelKey: 'employee.permCreateOrder', icon: faEdit, badgeClass: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' },
  { key: 'can_view_orders', labelKey: 'employee.permViewOrders', icon: faEye, badgeClass: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400' },
  { key: 'can_view_transactions', labelKey: 'employee.permViewTransactions', icon: faClipboardList, badgeClass: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' },
  { key: 'can_manage_settlement', labelKey: 'employee.permManageWallet', icon: faMoneyBill, badgeClass: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' },
  { key: 'can_view_settlement', labelKey: 'employee.permViewSettlement', icon: faEye, badgeClass: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' },
  { key: 'can_create_settlement', labelKey: 'employee.permCreateSettlement', icon: faMoneyBill, badgeClass: 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400' },
  { key: 'can_view_payouts', labelKey: 'employee.permViewPayout', icon: faEye, badgeClass: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400' },
  { key: 'can_create_payout', labelKey: 'employee.permCreatePayout', icon: faMoneyBill, badgeClass: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' },
  { key: 'can_send_callback', labelKey: 'employee.permSendCallback', icon: faClipboardList, badgeClass: 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400' },
  { key: 'can_view_analytics', labelKey: 'employee.permViewAnalytics', icon: faChartLine, badgeClass: 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400' },
  { key: 'can_view_tickets', labelKey: 'employee.permViewTickets', icon: faTicket, badgeClass: 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400' },
  { key: 'can_manage_employees', labelKey: 'employee.permManageEmployees', icon: faUsers, badgeClass: 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400' },
  { key: 'can_manage_settings', labelKey: 'employee.permManageSettings', icon: faGear, badgeClass: 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300' },
];

/**
 * เช็คสิทธิ์จาก permissions object — key ที่ไม่มีใช้ค่า fallback
 * รับ key เดี่ยวหรือ array (any-of)
 */
export const hasPermission = (
  permissions: Permissions | null | undefined,
  key: PermissionKey | PermissionKey[]
): boolean => {
  const keys = Array.isArray(key) ? key : [key];
  return keys.some((k) => {
    const value = permissions?.[k];
    return value === undefined ? PERMISSION_FALLBACKS[k] : value === true;
  });
};
